import { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import DashboardLayout from "../../controls/DashboardLayout";
import DashboardNavbar from "../../controls/DashboardNavbar";
import MDBox from "../../controls/MDBox";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { Card } from "react-bootstrap";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import DataTable from "../../controls/Tables/DataTable";
import { BuildingFillAdd, CalendarWeek, PencilSquare, Trash } from "react-bootstrap-icons";
import { Link, useNavigate } from "react-router-dom";
import { getData, deleteData } from "../../Utils/Http/api";
import { fetchPerfilActual } from "../../Utils/PerfilActual";
import { fetchUsuarios } from "../../Utils/Usuarios";
import { fetchOrganismos } from "../../Utils/Organismo";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import CalendarioMatriz from "./CalendarioMatriz";
import { DIAS, colorOrganismo, nombreDia } from "./calendarioComun";

// Calendario de Gestores: a qué organismo le toca gestionar a cada gestor cada día de la semana. Lo ve cualquier usuario; agregar,
// modificar y borrar solo lo hacen los perfiles con la casilla «Puede editar el Calendario de Gestores».
function CalendarioGestionListar() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [puedeEditar, setPuedeEditar] = useState(false);
  const [matrizAbierta, setMatrizAbierta] = useState(false);
  // Los filtros guardan el ID elegido (o null = todos), como los de Gestión Pre-Judicial. Las listas desplegables traen todos los
  // gestores (usuarios) y todos los organismos, no solo los que ya están en el calendario.
  const [usuarios, setUsuarios] = useState([]);
  const [organismos, setOrganismos] = useState([]);
  const [filtroGestor, setFiltroGestor] = useState(null);
  const [filtroDia, setFiltroDia] = useState(null);
  const [filtroOrganismo, setFiltroOrganismo] = useState(null);
  // Lo elegido en los desplegables recién filtra la tabla al tocar «Buscar»; hasta entonces la tabla sigue con los filtros anteriores.
  const [filtrosAplicados, setFiltrosAplicados] = useState({ gestor: null, dia: null, organismo: null });
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  const fetchData = async () => {
    try {
      const response = await getData("/CalendarioGestionListar");
      setItems(Array.isArray(response?.value) ? response.value : []);
    } catch (ex) {
      console.error("Error cargando el Calendario de Gestores:", ex);
      setSnackbar({ open: true, msg: "No se pudo cargar el calendario", severity: "error" });
    }
  };

  useEffect(() => {
    fetchData();
    fetchPerfilActual().then((perfil) => setPuedeEditar(!!perfil.puedeEditarCalendario));
  }, []);

  // Las listas desplegables, cargadas igual que en Gestión Pre-Judicial. El gestor testigo no gestiona casos: no entra al calendario.
  useEffect(() => {
    const cargarListas = async () => {
      try {
        const listaUsuarios = await fetchUsuarios();
        setUsuarios(listaUsuarios.filter((u) => !u.esTestigo).map((u) => ({ id: u.idUsuario, label: u.nombreCompleto || u.usuario, activo: u.activo })));
      } catch (ex) {
        console.error("Error cargando usuarios:", ex);
      }
      try {
        const listaOrganismos = await fetchOrganismos();
        setOrganismos(listaOrganismos.map((o) => ({ id: o.idOrganismo, label: o.descripcion, activo: o.activo })));
      } catch (ex) {
        console.error("Error cargando organismos:", ex);
      }
    };
    cargarListas();
  }, []);

  const filtrados = useMemo(
    () =>
      items.filter(
        (i) =>
          (!filtrosAplicados.gestor || i.idUsuario === filtrosAplicados.gestor) &&
          (!filtrosAplicados.dia || i.diaSemana === filtrosAplicados.dia) &&
          (!filtrosAplicados.organismo || i.idOrganismo === filtrosAplicados.organismo)
      ),
    [items, filtrosAplicados]
  );

  const handleBuscar = () => setFiltrosAplicados({ gestor: filtroGestor, dia: filtroDia, organismo: filtroOrganismo });

  const handleLimpiar = () => {
    setFiltroGestor(null);
    setFiltroDia(null);
    setFiltroOrganismo(null);
    setFiltrosAplicados({ gestor: null, dia: null, organismo: null });
  };

  // La tabla se arma una sola vez por cambio de datos: react-table reinicia página y orden cada vez que recibe un `table` nuevo, así que
  // pasarle uno distinto en cada render (por ejemplo al llegar las listas desplegables) re-renderiza el DataTable de más.
  const table = useMemo(() => {
    const columns = [
      { Header: "Gestor", accessor: "usuario", align: "left" },
      { Header: "Día", accessor: "dia", align: "left" },
      { Header: "Organismo", accessor: "organismo", align: "left" },
      ...(puedeEditar ? [{ Header: "Acciones", accessor: "action", align: "center" }] : []),
    ];

    const rows = filtrados.map((item) => {
      const color = colorOrganismo(item.idOrganismo);
      return {
        usuario: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.usuario}
            {item.usuarioActivo === false ? " (desactivado)" : ""}
          </MDTypography>
        ),
        dia: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {nombreDia(item.diaSemana)}
          </MDTypography>
        ),
        organismo: (
          <MDBox
            component="span"
            px={1.5}
            py={0.4}
            borderRadius="md"
            sx={{ backgroundColor: color.fondo, color: color.texto, fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}
          >
            {item.organismo}
          </MDBox>
        ),
        action: (
          <MDBox display="flex" gap={1} alignItems="center" justifyContent="center">
            <Link to={`../CalendarioGestionEdit/${item.idCalendarioGestion}`}>
              <MDButton variant="text" color="dark">
                <PencilSquare color="blue" />
              </MDButton>
            </Link>
            <MDButton variant="text" color="dark" onClick={() => setDeleteId(item.idCalendarioGestion)}>
              <Trash color="red" />
            </MDButton>
          </MDBox>
        ),
      };
    });

    return { columns, rows };
  }, [filtrados, puedeEditar]);

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      const res = await deleteData(`/CalendarioGestionDelete/${deleteId}`);
      if (res?.isSuccess) {
        setSnackbar({ open: true, msg: "Se quitó del calendario", severity: "success" });
        await fetchData();
      } else {
        setSnackbar({ open: true, msg: res?.error || "No se pudo quitar", severity: "error" });
      }
    } catch (ex) {
      setSnackbar({ open: true, msg: ex?.detail || ex?.message || "No se pudo quitar", severity: "error" });
    }
    setDeleteId(null);
    setDeleting(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="secondary" borderRadius="lg" coloredShadow="light">
                <MDTypography variant="h6" color="white">
                  Calendario de Gestores
                </MDTypography>
                <MDTypography variant="button" color="white" fontWeight="regular">
                  A qué organismo le toca gestionar a cada gestor cada día de la semana. El Disparador le asigna primero casos de ese organismo.
                </MDTypography>
              </MDBox>

              <MDBox pt={3} py={3} px={2}>
                <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  {puedeEditar && (
                    <MDButton onClick={() => navigate("/CalendarioGestionAdd")} variant="gradient" color="success" endIcon={<BuildingFillAdd />}>
                      Agregar
                    </MDButton>
                  )}
                  <MDButton onClick={() => setMatrizAbierta(true)} variant="gradient" color="info" endIcon={<CalendarWeek />}>
                    Ver calendario
                  </MDButton>
                </MDBox>

                <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center" mt={3}>
                  <Autocomplete
                    options={organismos}
                    getOptionLabel={(option) => option.label || ""}
                    getOptionDisabled={(option) => !option.activo}
                    value={organismos.find((o) => o.id === filtroOrganismo) || null}
                    onChange={(e, valor) => setFiltroOrganismo(valor ? valor.id : null)}
                    renderInput={(params) => <TextField {...params} label="Organismo" />}
                    sx={{ width: 400 }}
                  />
                  <Autocomplete
                    options={usuarios}
                    getOptionLabel={(option) => option.label || ""}
                    getOptionDisabled={(option) => !option.activo}
                    value={usuarios.find((u) => u.id === filtroGestor) || null}
                    onChange={(e, valor) => setFiltroGestor(valor ? valor.id : null)}
                    renderInput={(params) => <TextField {...params} label="Gestor" />}
                    sx={{ width: 300 }}
                  />
                  <Autocomplete
                    options={DIAS}
                    getOptionLabel={(option) => option.nombre || ""}
                    value={DIAS.find((d) => d.valor === filtroDia) || null}
                    onChange={(e, valor) => setFiltroDia(valor ? valor.valor : null)}
                    renderInput={(params) => <TextField {...params} label="Día" />}
                    sx={{ width: 200 }}
                  />
                  <MDButton variant="gradient" color="info" onClick={handleBuscar}>
                    Buscar
                  </MDButton>
                  <MDButton variant="text" color="dark" onClick={handleLimpiar}>
                    Limpiar
                  </MDButton>
                </MDBox>

                <DataTable
                  table={table}
                  isSorted={false}
                  showTotalEntries
                  canSearch={false}
                  noEndBorder
                  pagination={{ color: "info", variant: "gradient" }}
                />

                <CalendarioMatriz open={matrizAbierta} onClose={() => setMatrizAbierta(false)} filas={items} />

                <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
                  <DialogTitle>Confirmar eliminación</DialogTitle>
                  <DialogContent>¿Seguro que querés quitar este organismo del calendario del gestor?</DialogContent>
                  <DialogActions>
                    <MDButton variant="text" color="dark" onClick={() => setDeleteId(null)}>
                      Cancelar
                    </MDButton>
                    <MDButton variant="gradient" color="error" onClick={handleConfirmDelete} disabled={deleting}>
                      Quitar
                    </MDButton>
                  </DialogActions>
                </Dialog>

                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                  <MuiAlert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} elevation={6} variant="filled">
                    {snackbar.msg}
                  </MuiAlert>
                </Snackbar>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default CalendarioGestionListar;
